// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/donation.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { donationsApi } from "../../api/donations";
import type { Donation } from "../../api/types";
// override-block:imports BEGIN
import { createElement, type ReactNode } from "react";
import type { DonationStatus, DonationSource } from "../../api/types";
import type { ToneToken } from "../../lib/stage-tone-heuristic";
// CHG-1667 — slot helpers consume the page's Formatters bag.
import type { Formatters } from "../../contexts/IntlContext";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1325 — Tag taxonomy phase 3: Tags tab via EntityTagsSection.
import { EntityTagsSection } from "../../components/composition/EntityTagsSection";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<DonationStatus, string> = {
  pending: "Pending",
  succeeded: "Succeeded",
  refunded: "Refunded",
  failed: "Failed",
  charged_back: "Charged back",
  voided: "Voided",
  pending_review: "Pending review",
};
const SOURCE_LABEL: Record<DonationSource, string> = {
  dashboard: "Dashboard",
  widget: "Widget",
  kiosk: "Kiosk",
  "donate-page": "Donate page",
  session: "Giving session",
  "qr-code": "QR code",
  api: "API",
  external: "External",
  portal: "Portal",
  mobile: "Mobile",
  "mobile-qr": "Mobile QR",
  "mobile-shortcode": "Mobile shortcode",
  "mobile-app": "Mobile app",
  "signage-scan": "Signage scan",
};
// Status → stat card tone. Maps the admin status enum to one of the 10
// brand g-* tokens (declared in apps/dashboard-v2/src/styles/tokens.css).
// CHG-1314 — referenced via tone_resolver_fn on the entity-hint status card.
const STATUS_TONE: Record<DonationStatus, ToneToken> = {
  succeeded: "g-giving",
  pending: "g-home",
  refunded: "g-people",
  failed: "g-events",
  charged_back: "g-events", // dispute/problem, like failed
  voided: "g-people", // reversal, like refunded
  pending_review: "g-home", // awaiting action, like pending
};

// CHG-1314 — declarative resolver helpers consumed by the v2-gen
// generator via the status_cards.{value,tone}_resolver_fn fields.
function statusLabel(d: Donation): string {
  return STATUS_LABEL[d.status] ?? d.status;
}
function statusTone(d: Donation): ToneToken | undefined {
  return STATUS_TONE[d.status];
}
function sourceLabel(d: Donation): string | undefined {
  return d.source ? (SOURCE_LABEL[d.source] ?? d.source) : undefined;
}

function donorLabel(d: Donation): string {
  if (d.anonymous) return "Anonymous";
  if (d.donor_name) return d.donor_name;
  if (d.donor_email) return d.donor_email;
  return d.donor_id ? d.donor_id.slice(0, 8) + "…" : "Unknown donor";
}

// CHG-1667 — processorLabel(d) for the new "Processor" status card.
// Resolves the donation's payment processor to a human-readable label.
// Maps known processors (stripe/paypal/manual/etc.); falls back to the
// raw payment_processor string or em-dash.
function processorLabel(d: Donation): string {
  const PROC: Record<string, string> = {
    stripe: "Stripe",
    paypal: "PayPal",
    manual: "Manual",
    cash: "Cash",
    check: "Check",
    bank_transfer: "Bank transfer",
    import: "Import",
  };
  const raw = d.payment_processor;
  if (!raw) return "—";
  return PROC[raw] ?? raw;
}

// CHG-1667 — 4 custom_render slot helpers (entity-hint tabs.*.custom_render).
// Each receives the Donation record + Formatters and returns a ReactNode.
// Signature contract: (r: Donation, fmt: Formatters) => ReactNode.

function slotDesignationBreakdown(d: Donation, fmt: Formatters): ReactNode {
  return createElement(
    "div",
    { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
    createElement("div", null, "Donor: ", donorLabel(d)),
    createElement(
      "div",
      null,
      "Amount: ",
      fmt.formatCurrency(d.amount_minor_units, d.currency),
    ),
    d.fees_minor_units != null
      ? createElement("div", null, "Fees: ", fmt.formatCurrency(d.fees_minor_units, d.currency))
      : null,
    d.net_minor_units != null
      ? createElement("div", null, "Net: ", fmt.formatCurrency(d.net_minor_units, d.currency))
      : null,
    createElement("div", null, "Source: ", sourceLabel(d) ?? "—"),
    createElement("div", null, "Payment method: ", d.payment_method ?? "—"),
    createElement("div", null, "Designation: ", d.designation_name ?? d.designation_id ?? "—"),
    createElement("div", null, "Campaign: ", d.campaign_name ?? "—"),
    d.session_name
      ? createElement("div", null, "Session: ", d.session_name)
      : null,
    createElement(
      "div",
      null,
      "Recorded: ",
      fmt.formatDate(d.created_at, "long"),
    ),
    d.external_id
      ? createElement(
          "div",
          { style: { marginTop: 8, color: "var(--color-text-muted)" } },
          "External ID: ",
          d.external_id,
        )
      : null,
    d.idempotency_key
      ? createElement(
          "div",
          { style: { color: "var(--color-text-muted)" } },
          "Idempotency: ",
          d.idempotency_key,
        )
      : null,
  );
}

function slotReceiptPdfEmbed(d: Donation, _fmt: Formatters): ReactNode {
  const pdfUrl = d.receipt_pdf_url;
  return createElement(
    "div",
    { className: "card", style: { padding: 16 } },
    createElement(
      "div",
      { style: { fontWeight: 600, marginBottom: 8, fontSize: 13 } },
      "Donation receipt",
    ),
    pdfUrl
      ? createElement(
          "div",
          null,
          createElement("iframe", {
            src: pdfUrl,
            title: "Receipt PDF",
            style: {
              width: "100%",
              height: 480,
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              marginBottom: 12,
            },
          }),
          createElement(
            "a",
            {
              href: pdfUrl,
              target: "_blank",
              rel: "noopener noreferrer",
              style: { fontSize: 13, color: "var(--accent, #0066cc)" },
            },
            "Download PDF",
          ),
        )
      : createElement(
          "div",
          { style: { fontSize: 13, color: "var(--color-text-muted)" } },
          `No receipt PDF available yet for ${d.id}. Use the "Send receipt" header action to generate + deliver one.`,
        ),
  );
}

function slotInitiateRefundModal(d: Donation, fmt: Formatters): ReactNode {
  const refundedMinorUnits = d.refunded_minor_units ?? 0;
  const remaining = d.amount_minor_units - refundedMinorUnits;
  return createElement(
    "div",
    { className: "card", style: { padding: 16 } },
    createElement(
      "div",
      { style: { fontWeight: 600, marginBottom: 8, fontSize: 13 } },
      "Refunds",
    ),
    createElement(
      "div",
      { style: { fontSize: 13, lineHeight: 1.6 } },
      createElement("div", null, "Original: ", fmt.formatCurrency(d.amount_minor_units, d.currency)),
      createElement("div", null, "Refunded: ", fmt.formatCurrency(refundedMinorUnits, d.currency)),
      createElement("div", null, "Remaining: ", fmt.formatCurrency(remaining, d.currency)),
    ),
    createElement(
      "p",
      {
        style: {
          marginTop: 12,
          fontSize: 12,
          color: "var(--color-text-muted)",
        },
      },
      `Use the "Mark refunded" header action to initiate a refund. Refund history fetched from GET /donations/${d.id}/refunds (full timeline UI ships in a follow-up CHG).`,
    ),
  );
}

function slotAuditTimeline(d: Donation, _fmt: Formatters): ReactNode {
  return createElement(
    "div",
    { className: "card", style: { padding: 16 } },
    createElement(
      "div",
      { style: { fontWeight: 600, marginBottom: 8, fontSize: 13 } },
      "Audit trail",
    ),
    createElement(
      "div",
      { style: { fontSize: 13, color: "var(--color-text-muted)", lineHeight: 1.6 } },
      `Chronological status / receipt / edit events for donation ${d.id}. Fetched via GET /donations/${d.id}/timeline?kind=audit (bridge endpoint; canonical /audit pending). Each row carries actor + diff snapshot. Full timeline UI ships in a follow-up CHG.`,
    ),
  );
}
// override-block:helpers END

export const donationDetailConfig: DetailPageConfig<Donation> = {
  idParam: "donationId",
  queryKey: (id: string) => ["donations", "detail", id] as const,
  queryFn: (id: string) => donationsApi.getDonation(id),
  backTo: "/donations",
  // override-block:title BEGIN
  // Title synthesis: amount_minor_units alone is a poor title; the slot
  // synthesizes "$50.00 USD · Widget" from amount + source label.
  title: (d) => {
    const amountStr = `${(d.amount_minor_units / 100).toFixed(2)} ${d.currency}`;
    const source = sourceLabel(d);
    return source ? `${amountStr} · ${source}` : amountStr;
  },
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (d) => donorLabel(d),
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    {
      id: "amount",
      label: "Amount",
      value: r.amount_minor_units,
      format: "currency",
      currency: r.currency,
      tone: "g-giving",
    },
    {
      id: "status",
      label: "Status",
      value: statusLabel(r),
      format: "text",
      tone: statusTone(r) ?? "g-events",
    },
    {
      id: "processor",
      label: "Processor",
      value: processorLabel(r),
      format: "text",
      tone: "g-channels",
    },
    {
      id: "refunded",
      label: "Refunded",
      value: r.refunded_minor_units,
      format: "currency",
      currency: r.currency,
      tone: "g-people",
    },
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    { id: "view-receipt", label: "View receipt", variant: "secondary", permissionGate: toGate(GATE_CATALOG.donation_detail["view-receipt"], "disable") },
    { id: "send-receipt", label: "Send receipt", variant: "primary", permissionGate: toGate(GATE_CATALOG.donation_detail["send-receipt"], "hide") },
    { id: "mark-refunded", label: "Mark refunded", variant: "ghost", permissionGate: toGate(GATE_CATALOG.donation_detail["mark-refunded"], "disable") },
    // override-block:actions END
  ],
  tabs: [
    {
      id: "overview",
      label: "Overview",
      render: (r, fmt) => slotDesignationBreakdown(r, fmt),
    },
    {
      id: "receipt",
      label: "Receipt",
      render: (r, fmt) => slotReceiptPdfEmbed(r, fmt),
    },
    {
      id: "refunds",
      label: "Refunds",
      permission: "alora-giving.donation.refund",
      render: (r, fmt) => slotInitiateRefundModal(r, fmt),
    },
    {
      id: "audit",
      label: "Audit",
      permission: "alora-giving.donation.audit_read",
      render: (r, fmt) => slotAuditTimeline(r, fmt),
    },
    // override-block:custom-tabs BEGIN
    // CHG-1667 / ITER-3776 — overview + receipt + refunds + audit tabs are
    // now generator-emitted from entity-hint `pages.detail_v2.tabs` via
    // custom_render slot helpers (slotDesignationBreakdown,
    // slotReceiptPdfEmbed, slotInitiateRefundModal, slotAuditTimeline,
    // defined in override-block:helpers above). Only the Tags tab remains
    // hand-rolled here, as it depends on the EntityTagsSection composition
    // primitive (CHG-1325) which the generator doesn't model yet — file a
    // follow-up DRIFT before retiring the override-block.
    //
    // CHG-1325 — Tag taxonomy phase 3. Tags applied to this donation.
    {
      id: "tags",
      label: "Tags",
      render: (d) =>
        createElement(EntityTagsSection, {
          entityType: "donation",
          entityId: d.id,
        }),
    },
    // override-block:custom-tabs END
  ],
};
